




















































































import { Component, mixins, Watch, On } from 'nuxt-property-decorator'
import { Context } from '@nuxt/types'
import { gsap, ScrollTrigger, ScrollSmoother } from 'gsap'
import frames from '@/utils/FrameToTime'

import DeviceMixin from '@/mixins/device'
import PageTransitionMixin from '@/mixins/transitions/artist'

type ImageMetas = {
  width: number
  height: number
  ratio: number
}
type ImagesMetas = { [filename: string]: ImageMetas }

@Component
export default class PageArtist extends mixins(PageTransitionMixin, DeviceMixin) {
  artwork: Artwork
  relatedArtworks: Artwork[]

  $refs!: {
    label: HTMLElement,
    informations: HTMLElement
  }

  $scrollSmoother: ScrollSmoother = null
  isPageReady = false
  showRelated = false
  imagesMetas: ImagesMetas = {}
  zIndex = 1

  get translationPlaceholders() {
    const { title, artist_name, country } = this.artwork
    return { title, artist_name, country }
  }

  @Watch('showRelated')
  onShowRelated(show: boolean) {
    gsap.to(gsap.utils.toArray('.asset', this.$el), {
      scale: show ? 0.9 : 1,
      duration: frames(27),
      ease: 'cubic.inOut',
      stagger: () => frames(gsap.utils.random(0, 8)),
      delay: show ? 0 : frames(4)
    })
  }

  async validate({ params, store }: Context) {
    await store.dispatch('fetch')

    const getArtwork: (id: Artwork['id']) => Artwork = store.getters['getArtwork']
    return !!getArtwork(params.id as string)
  }

  async asyncData({ params, store }: Context) {
    await store.dispatch('fetch')

    const getArtwork: (id: Artwork['id']) => Artwork = store.getters['getArtwork']
    const artwork = getArtwork(params.id as string)
    const relatedArtworks = artwork?.related_artists?.map(getArtwork).filter(Boolean)

    return { artwork, relatedArtworks }
  }

  head() {
    const title = this.$t('common.artwork.meta_title', this.translationPlaceholders)
    const description = this.$t('common.artwork.meta_description', this.translationPlaceholders)
    const image = `${this.$config.baseUrl}/assets/images/${this.artwork.assets.main}`

    return {
      title,
      meta: [
        { hid: 'description', name: 'description', content: description },
        // Open Graph
        { hid: 'og:title', property: 'og:title', content: title },
        { hid: 'og:description', property: 'og:description', content: description },
        { hid: 'og:image', property: 'og:image', content: image },
        // Twitter Card
        { hid: 'twitter:title', name: 'twitter:title', content: title },
        { hid: 'twitter:description', name: 'twitter:description', content: description },
        { hid: 'twitter:image', property: 'twitter:image', content: image },
      ]
    }
  }

  beforeDestroy() {
    const stPin = ScrollTrigger.getById(`pin-${this.artwork.id}`)
    stPin?.kill(false)
  }

  onScrollReady(scrollSmoother: ScrollSmoother) {
    this.$scrollSmoother = scrollSmoother
  }

  @On('page:ready')
  async onPageReady() {
    this.isPageReady = true

    // wait until assets DOM elements exist (issue with generated pages)
    const getAssets = () => gsap.utils.toArray('.asset > *', this.$el) as HTMLElement[]
    let assets = getAssets()
    while (!assets.length) {
      await this.$nextTick()
      assets = getAssets()
    }

    ScrollTrigger.matchMedia({
      '(prefers-reduced-motion: no-preference)': () => {
        // add wiggle effects to assets's content
        let wiggles: GSAPTimeline = gsap.effects.wiggle(assets)

        return () => {
          wiggles.kill()
          wiggles = null
        }
      },
      [this.$breakpoints.mq('lg')]: () => {
        // pin artwork label
        ScrollTrigger.create({
          id: `pin-${this.artwork.id}`,
          trigger: this.$refs.label,
          pin: true,
          pinSpacing: false,
          start: 0,
          end: 'max'
        })

        // fade out artwork description once scroll
        gsap.to(this.$refs.informations, {
          opacity: 0,
          duration: frames(10),
          ease: 'linear',
          scrollTrigger: {
            start: 5,
            toggleActions: 'play none none reverse'
          }
        })
      },
      'all': () => {
        gsap.utils.toArray('.text', this.$el).forEach((trigger: HTMLElement, i) => {
          const offset = this.isMobile ? 0 : (i ? 2 : 3) * (gsap.getProperty(trigger, 'line-height') as number)
          const animation = gsap.from(trigger, {
            opacity: 0,
            duration: frames(30),
            ease: 'linear'
          })

          // on scroll down
          ScrollTrigger.create({
            trigger,
            animation,
            start: `top+=${offset} bottom`,
            end: `bottom center`,
            toggleActions: 'play reverse play reverse'
          })

          // on scroll up
          ScrollTrigger.create({
            trigger,
            animation,
            start: `top center`,
            end: `bottom-=${offset} top`,
            toggleActions: 'play reverse play reverse'
          })
        })

        // reveal assets on scroll
        gsap.utils.toArray('.asset', this.$el).forEach((trigger: HTMLElement) => {
          ScrollTrigger.isInViewport(trigger) ||
          gsap.to(trigger, {
            opacity: 1,
            scale: 1,
            duration: frames(27),
            ease: 'cubic.inOut',
            scrollTrigger: {
              trigger,
              start: '25% bottom',
              once: true,
              invalidateOnRefresh: true
            }
          })
        })
      }
    })

    this.isDesktop && this.setupParallax()
  }

  setupParallax() {
    ScrollTrigger.matchMedia({
      '(prefers-reduced-motion: no-preference)': () => {
        const assets = gsap.utils.toArray('.asset', this.$el) as HTMLElement[]
        this.$scrollSmoother?.effects(assets)

        ScrollTrigger.refresh()

        // restore elements default position
        assets.forEach((el) => {
          const getter = gsap.getProperty(el)
          const y = getter('y')

          const styles: gsap.TweenVars = {
            top: -y
          }

          const position = getter('position')
          if (position === 'static') {
            styles.position = 'relative'
          }

          gsap.set(el, styles)
        })

        ScrollTrigger.refresh()

        return () => {
          this.$scrollSmoother?.effects().forEach(effect => effect.kill())
        }
      }
    })
  }

  getImageMetas(filename: string) {
    return this.imagesMetas?.[filename] || { width: 0, height: 0, ratio: 0 }
  }
  setImageMetas(filename: string, metas: ImageMetas) {
    this.$set(this.imagesMetas, filename, metas)
  }

  getImageStyles(asset: string) {
    const { ratio } = this.getImageMetas(asset)
    return {
      '--aspect-ratio': ratio || 1
    }
  }

  onHoverAsset({ target }: { target: HTMLImageElement }) {
    target.style.zIndex = (++this.zIndex).toString()
  }
}
